import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Globe2, ShieldCheck, Loader2, Send, FileDown } from "lucide-react";
import Reveal from "./Reveal";
import SectionHeading from "./SectionHeading";
import { EMAIL, PHONE_DISPLAY, PHONE_TEL } from "../constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_TYPES = ["General Inquiry", "Private Event", "Corporate", "Venue Residency", "Festival", "Studio Session"];

export default function Booking() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: EVENT_TYPES[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/booking`, form);
      toast.success("Inquiry sent — Hulda will get back to you shortly.");
      setForm({ name: "", email: "", phone: "", event_type: EVENT_TYPES[0], message: "" });
    } catch {
      toast.error("Could not send the inquiry. Please email golden.hp85@gmail.com directly.");
    } finally {
      setSending(false);
    }
  };

  const CONTACT = [
    { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, id: "booking-email-link" },
    { icon: Phone, label: "Phone", value: PHONE_DISPLAY, href: `tel:${PHONE_TEL}`, id: "booking-phone-link" },
    { icon: Globe2, label: "Availability", value: "Worldwide contract deployment", href: null, id: "booking-availability" },
    { icon: ShieldCheck, label: "Travel", value: "Valid passport", href: null, id: "booking-passport" },
  ];

  return (
    <section id="booking" className="relative py-24 lg:py-32 overflow-hidden" data-testid="booking-section">
      <div className="pointer-events-none absolute -bottom-32 -left-32 w-[540px] h-[540px] rounded-full bg-hpink/15 blur-[150px]" />
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
        <SectionHeading index="08" eyebrow="Booking & Talent Inquiry" title="Book Hulda" />

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <Reveal>
            <div className="space-y-4">
              {CONTACT.map((c) => {
                const inner = (
                  <>
                    <span className="w-11 h-11 shrink-0 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-hpink/50 transition-colors">
                      <c.icon size={17} className="text-hpink" />
                    </span>
                    <span>
                      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-hdim">{c.label}</span>
                      <span className="block mt-1 text-base sm:text-lg font-semibold text-white break-words">{c.value}</span>
                    </span>
                  </>
                );
                return c.href ? (
                  <a
                    key={c.label}
                    href={c.href}
                    data-testid={c.id}
                    className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-all duration-300 hover:bg-white/[0.06]"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={c.label} data-testid={c.id} className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                    {inner}
                  </div>
                );
              })}
              <a
                href="/hulda-perez-press-kit.pdf"
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="download-press-kit"
                className="group flex items-center gap-4 rounded-2xl border border-hblue/40 bg-hblue/10 p-5 transition-all duration-300 hover:bg-hblue/20"
              >
                <span className="w-11 h-11 shrink-0 rounded-2xl bg-hblue/15 border border-hblue/40 flex items-center justify-center">
                  <FileDown size={17} className="text-hblue" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-hdim">One-Sheet</span>
                  <span className="block mt-1 text-base sm:text-lg font-semibold text-white">Download Press Kit (PDF)</span>
                </span>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              onSubmit={submit}
              className="rounded-3xl border border-white/10 bg-white/[0.04] p-7 lg:p-9 space-y-5"
              data-testid="booking-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="bk-name" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-hdim mb-2">
                    Name *
                  </label>
                  <input
                    id="bk-name"
                    required
                    value={form.name}
                    onChange={set("name")}
                    data-testid="booking-name-input"
                    className="w-full rounded-xl bg-ink/60 border border-white/12 px-4 py-3 text-sm text-white placeholder:text-hdim focus:outline-none focus:border-hpink focus:ring-2 focus:ring-hpink/30 transition"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="bk-email" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-hdim mb-2">
                    Email *
                  </label>
                  <input
                    id="bk-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    data-testid="booking-email-input"
                    className="w-full rounded-xl bg-ink/60 border border-white/12 px-4 py-3 text-sm text-white placeholder:text-hdim focus:outline-none focus:border-hpink focus:ring-2 focus:ring-hpink/30 transition"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="bk-phone" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-hdim mb-2">
                    Phone
                  </label>
                  <input
                    id="bk-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="booking-phone-input"
                    className="w-full rounded-xl bg-ink/60 border border-white/12 px-4 py-3 text-sm text-white placeholder:text-hdim focus:outline-none focus:border-hpink focus:ring-2 focus:ring-hpink/30 transition"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label htmlFor="bk-type" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-hdim mb-2">
                    Event Type
                  </label>
                  <select
                    id="bk-type"
                    value={form.event_type}
                    onChange={set("event_type")}
                    data-testid="booking-type-select"
                    className="w-full rounded-xl bg-ink/60 border border-white/12 px-4 py-3 text-sm text-white focus:outline-none focus:border-hpink focus:ring-2 focus:ring-hpink/30 transition"
                  >
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-ink">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="bk-message" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-hdim mb-2">
                  Details *
                </label>
                <textarea
                  id="bk-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="booking-message-input"
                  className="w-full rounded-xl bg-ink/60 border border-white/12 px-4 py-3 text-sm text-white placeholder:text-hdim focus:outline-none focus:border-hpink focus:ring-2 focus:ring-hpink/30 transition resize-none"
                  placeholder="Date, venue, set length, anything else…"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="booking-submit-button"
                className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-hpink hover:bg-horange disabled:opacity-60 disabled:hover:bg-hpink transition-colors duration-300 px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] font-bold glow-pink"
              >
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {sending ? "Sending…" : "Send Inquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
